// Third-party
import React, { useEffect } from 'react';
import { RouteComponentProps, Route, Redirect, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

// Components
import AdminEnterprise from './enterprise/AdminEnterprise';
import AdminDataPortal from './admin-portal/AdminDataPortal';
import AdminGroups from './group/AdminGroups';
import { SecondaryNavigation } from '@/views/components/navigation';
import { Header, OverlappingBackground } from '@/views/components/layout';
import { ErrorBoundary } from '@/views/components/errors';

// Utils
import { FormattedMessage, formatMessage } from '@/utils/i18n';
import { getAdminUserEnterpriseDetails } from '@/utils/admin';
import { PERMISSIONS, userHasPermissionEnabled, userHasPermission } from '@/utils/permissions';

// Actions
import { ReduxState } from '@/redux/modules/types';
import { getEnterpriseAdminInfo, saveEnterpriseInfos } from '@/redux/modules/enterprise';
import { addEnterpriseGroup } from '@/redux/modules/enterprise/admin/groups';
import { selectDelegateMode } from '@/redux/modules/auth/selectors';
import { setPortfolioEnterprise } from '@/redux/modules/edit-portfolio';

// Types
import AppRegion from '@/types/app-region';
import EnterpriseAdminDetails from '@/types/enterprise-admin-details';
import EnterpriseDetails from '@/types/enterprise-detail-info';
import EnterpriseInfos from '@/types/enterprise-infos';
import EnterpriseGroup from '@/types/enterprise-group';
import User from '@/types/user';

enum AdminTabs {
  DATA_PORTAL = '/admin/data-upload',
  ENTERPRISE = '/admin/enterprise',
  GROUPS = '/admin/groups',
}

type StateProps = {
  adminDetails: EnterpriseAdminDetails;
  delegateMode: boolean;
  enterprise: EnterpriseDetails;
  user: User;
};

type DispatchProps = {
  addEnterpriseGroup: (enterpriseId: string, name: string) => Promise<EnterpriseGroup>;
  getEnterpriseAdminInfo: (id: string) => Promise<EnterpriseAdminDetails>;
  saveEnterpriseInfos: (id: string, infos: EnterpriseInfos) => Promise<EnterpriseInfos>;
  setPortfolioEnterprise: () => void;
};

type Props = RouteComponentProps & StateProps & DispatchProps;
const Wrapper = ({ children }: { children: React.ReactNode }) => <div className="content">{children}</div>;

const Admin: React.FC<Props> = (props: Props) => {
  const { adminDetails, delegateMode, enterprise, match, user, addEnterpriseGroup, getEnterpriseAdminInfo, saveEnterpriseInfos, setPortfolioEnterprise } =
    props;

  const getAdminRoutes = () => {
    const appRegion: AppRegion = user != null ? user.appConfig.appRegion : (AppRegion.CAN as AppRegion);
    const isAdminPortalAccess = userHasPermission(user, PERMISSIONS.ENTERPRISE_ADMIN_DATA_UPLOAD);

    return [
      {
        value: AdminTabs.ENTERPRISE,
        label: formatMessage('Admin.Nav.Enterprise'),
        path: `${match.path}/enterprise`,
        icon: 'icon-business-center',
        hidden:
          !userHasPermissionEnabled(
            user,
            PERMISSIONS.ENTERPRISE_ADMIN,
            PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MODELS,
            PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS,
          ) ||
          delegateMode ||
          adminDetails == null ||
          enterprise == null,
        render: (props: RouteComponentProps) => (
          <Wrapper>
            <AdminEnterprise {...props} user={user} enterprise={enterprise} onSave={onSaveEnterpriseInfos} appRegion={appRegion} />
          </Wrapper>
        ),
      },
      {
        value: AdminTabs.GROUPS,
        label: formatMessage('Admin.Nav.Groups'),
        hidden:
          !userHasPermissionEnabled(
            user,
            PERMISSIONS.ENTERPRISE_ADMIN_GROUPS,
            PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MODELS,
            PERMISSIONS.ENTERPRISE_ADMIN_GROUPS_MEMBERS,
          ) ||
          delegateMode ||
          adminDetails == null ||
          enterprise == null,
        path: `${match.path}/groups`,
        icon: 'icon-people',
        render: (props: RouteComponentProps) => (
          <Wrapper>
            <AdminGroups
              {...props}
              user={user}
              enterprise={enterprise}
              adminDetails={adminDetails}
              groups={adminDetails?.groups || []}
              onAddGroup={onAddGroupToEnterprise}
            />
          </Wrapper>
        ),
      },
      {
        value: AdminTabs.DATA_PORTAL,
        label: formatMessage('Admin.Nav.DataUpload'),
        path: `${match.path}/data-upload`,
        icon: 'icon-drive-folder-upload',
        hidden: !isAdminPortalAccess || delegateMode,
        render: (props: RouteComponentProps) => (
          <Wrapper>
            <AdminDataPortal />
          </Wrapper>
        ),
      },
    ].filter(route => !route.hidden);
  };

  const onAddGroupToEnterprise = (name: string): Promise<EnterpriseGroup> => {
    if (!enterprise) {
      return Promise.reject();
    }

    // Reload enterprise's data after adding a new group
    return addEnterpriseGroup(enterprise._id, name).then(newGroup => newGroup);
  };

  // Enterprise Admin Actions
  const onSaveEnterpriseInfos = (infos: EnterpriseInfos) => {
    if (!enterprise) {
      return Promise.reject();
    }
    return saveEnterpriseInfos(enterprise._id, infos);
  };

  useEffect(() => {
    const userEnterprise = getAdminUserEnterpriseDetails(user);

    if (userEnterprise) {
      getEnterpriseAdminInfo(userEnterprise._id);
      setPortfolioEnterprise();
    }
  }, [user, getEnterpriseAdminInfo, setPortfolioEnterprise]);

  const routes = getAdminRoutes();

  return (
    <>
      <Header />
      <OverlappingBackground />
      <main id="content" className="admin">
        <div className="admin-navigation d-flex align-items-center">
          <h1 className="h3 title">
            <FormattedMessage id="Admin.PageTitle" />
          </h1>
          <SecondaryNavigation className="flex-grow-1" items={routes} />
        </div>

        <ErrorBoundary>
          {routes.map(route => (
            <Route key={route.value} path={route.path} render={route.render} />
          ))}
          <Route exact path={`${match.path}/`} render={() => <Redirect to={routes.length > 0 ? routes[0].path : '/admin'} />} />
        </ErrorBoundary>
      </main>
    </>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  adminDetails: state.enterprise.admin,
  delegateMode: selectDelegateMode(state),
  enterprise: state.enterprise.details,
});

export default withRouter(
  connect(mapStateToProps, {
    addEnterpriseGroup,
    getEnterpriseAdminInfo,
    saveEnterpriseInfos,
    setPortfolioEnterprise,
  })(Admin),
);
